/**
 * MusicSheet — TikTok-style bottom sheet for picking a track.
 *
 * Lives in its own file so /social/create's `SocialCreate` can stay
 * focused on capture / submit. Used by `SocialPages.jsx` and could be
 * reused on /studio/live for "background music" later.
 *
 * Props:
 *   onClose : () => void
 *   onPick  : (track) => void   — picks the track + closes the sheet
 *   current : { id } | null      — track currently attached (for "Picked" badge)
 */
import React, { useEffect, useRef, useState } from "react";
import { Search, X, Play, Mic, Check } from "lucide-react";
import api from "../../lib/api";

export default function MusicSheet({ onClose, onPick, current }) {
  const [q, setQ] = useState("");
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);
  const previewRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    const ep = q.trim() ? `/social/music?q=${encodeURIComponent(q.trim())}` : "/social/music/trending";
    api.get(ep)
      .then((r) => setTracks(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTracks([]))
      .finally(() => setLoading(false));
  }, [q]);

  // Stop preview when sheet unmounts
  useEffect(() => () => {
    if (previewRef.current) { previewRef.current.pause(); previewRef.current.src = ""; }
  }, []);

  const togglePreview = (t) => {
    const el = previewRef.current;
    if (!el) return;
    if (playing === t.id) { el.pause(); setPlaying(null); return; }
    el.src = t.url; el.play().catch(() => {}); setPlaying(t.id);
  };

  return (
    <div data-testid="create-music-sheet" className="absolute inset-0 z-30 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-h-[78vh] bg-[#0a0a0a] rounded-t-3xl border-t border-white/10 flex flex-col"
      >
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <span className="w-10" />
          <div className="text-white font-black text-sm uppercase tracking-[0.2em]">Pick a track</div>
          <button data-testid="music-sheet-close" onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 bg-white/8 border border-white/15 rounded-full px-3 py-2">
            <Search className="w-4 h-4 text-white/60" />
            <input
              data-testid="music-sheet-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search music"
              className="flex-1 bg-transparent text-white text-sm placeholder-white/40 outline-none"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-4">
          {loading && <div className="text-white/50 text-center py-8 text-sm">Loading…</div>}
          {!loading && tracks.length === 0 && (
            <div data-testid="music-sheet-empty" className="text-white/50 text-center py-12 text-sm">No tracks. Try a different search.</div>
          )}
          <ul className="space-y-1">
            {tracks.map((t) => {
              const active = current?.id === t.id;
              const isPlaying = playing === t.id;
              return (
                <li key={t.id} data-testid={`music-track-${t.id}`} className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/5">
                  <button
                    data-testid={`music-preview-${t.id}`}
                    onClick={() => togglePreview(t)}
                    className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-rose-700 flex items-center justify-center shrink-0"
                  >
                    {isPlaying ? <Mic className="w-5 h-5 text-white animate-pulse" /> : <Play className="w-5 h-5 text-white" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-bold text-sm truncate">{t.title}</div>
                    <div className="text-white/50 text-xs truncate">{t.artist} · 0:{String(t.duration_sec || 30).padStart(2, "0")}</div>
                  </div>
                  <button
                    data-testid={`music-pick-${t.id}`}
                    onClick={() => onPick(t)}
                    className={`shrink-0 text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-full inline-flex items-center gap-1 ${active ? "bg-white text-black" : "bg-red-500 hover:bg-red-600 text-white"}`}
                  >
                    {active ? <><Check className="w-3.5 h-3.5" /> Picked</> : "Use"}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <audio ref={previewRef} onEnded={() => setPlaying(null)} className="hidden" />
      </div>
    </div>
  );
}
